import { useQuery, useMutation } from '@tanstack/react-query';

import Axios from 'axios';
import { queryClient } from 'QueryClientProvider';

import { forceReloadCreditNote } from '@hooks/API/UseCreditNotes';

const getContactInvoices = (ID) => {
    return Axios.get(`/contacts/invoices/${ID}`);
};

const useInvoices = (ID) => {
    return useQuery({
        queryKey: ['invoices-contact', ID],
        queryFn: () => getContactInvoices(ID),
        enabled: true,
        refetchOnWindowFocus: false,
        staleTime: 300000,
        cacheTime: Infinity,
        retry: false,
    });
};

const forceReloadInvoices = (ID) => queryClient.invalidateQueries({ queryKey: ['invoices-contact', ID] });

const getInvoice = (ID) => {
    return Axios.get(`/Invoices/${ID}`);
};

const useInvoice = (ID) => {
    return useQuery({
        queryKey: ['invoices', ID],
        queryFn: () => getInvoice(ID),
        staleTime: 10000,
        cacheTime: 10000,
        retry: false,
        enabled: ID ? true : false,
    });
};

const forceReloadInvoice = (ID) => queryClient.invalidateQueries({ queryKey: ['invoices', ID] });

const getInvoicePrints = (ID) => {
    return Axios.post('/docversions/getfiltered', { InvoiceID: ID });
};

const useInvoicePrints = (ID) => {
    return useQuery({
        queryKey: ['invoice-prints', ID],
        queryFn: () => getInvoicePrints(ID),
        enabled: ID ? true : false,
        staleTime: 10000,
        cacheTime: 10000,
        retry: false,
    });
};

const forceReloadInvoicePrints = (ID) => queryClient.invalidateQueries({ queryKey: ['invoice-prints', ID] });

const getInvoicesSearchParams = (filters) => {
    if (filters) {
        let searchParams = { ...filters };
        if (filters.Period) {
            searchParams = {
                ...searchParams,
                FromDate: filters.Period[0],
                ToDate: filters.Period[1],
            };
            delete searchParams.Period;
        }
        return searchParams;
    }
    return null;
};

const onSearchInvoices = (searchParams) => {
    return Axios.post('/Invoices/Search', searchParams);
};

const useSearchInvoices = (filters) => {
    const searchParams = getInvoicesSearchParams(filters);
    return useQuery({
        queryKey: ['invoices-search'],
        queryFn: () => onSearchInvoices(searchParams),
        enabled: true,
        refetchOnWindowFocus: false,
        staleTime: 300000,
        cacheTime: Infinity,
        retry: false,
    });
};

const refetchInvoicesSearch = async (filters) => {
    const searchParams = getInvoicesSearchParams(filters);
    try {
        await queryClient.fetchQuery({ queryKey: ['invoices-search'], queryFn: () => onSearchInvoices(searchParams) });
    } catch (error) {
        console.error(error);
    }
};

const forceReloadSearchInvoices = (area) => queryClient.invalidateQueries({ queryKey: ['invoices-search', area] });

const onAddNewInvoice = ({ data }) => {
    return Axios.post(`/Invoices`, data);
};

const useAddNewInvoice = () => {
    return useMutation({
        mutationFn: onAddNewInvoice,
        onSuccess: () => {},
    });
};

const onUpdateInvoice = ({ data }) => {
    return Axios.put(`/Invoices`, data);
};

const useUpdateInvoice = () => {
    return useMutation({
        mutationFn: onUpdateInvoice,
        onSuccess: (_, variables) => {
            forceReloadInvoice(variables.data.invoiceID);
        },
    });
};

const onSortRows = ({ ID, data }) => {
    return Axios.put(`/Invoices/rows/SetOrder/${ID}`, data);
};

const useSortRows = () => {
    return useMutation({
        mutationFn: onSortRows,
        onSuccess: (_, variables) => {
            forceReloadInvoice(variables.ID);
        },
    });
};

const onGroupRows = ({ ID, data }) => {
    return Axios.put(`/Invoices/rows/Group/${ID}`, data);
};

const useGroupRows = () => {
    return useMutation({
        mutationFn: onGroupRows,
        onSuccess: (_, variables) => {
            forceReloadInvoice(variables.ID);
        },
    });
};

const onUnGroupRows = ({ ID, data }) => {
    return Axios.put(`/Invoices/rows/SplitGroup/${ID}`, data);
};

const useUnGroupRows = () => {
    return useMutation({
        mutationFn: onUnGroupRows,
        onSuccess: (_, variables) => {
            forceReloadInvoice(variables.ID);
        },
    });
};

const onAddManual = ({ data }) => {
    return Axios.post(`/Invoices/rows/AddManual`, data);
};

const useAddManual = () => {
    return useMutation({
        mutationFn: onAddManual,
        onSuccess: (_, variables) => {
            forceReloadInvoice(variables.data.InvoiceID);
        },
    });
};

// Adds a single statement row to an existing draft invoice
const onAddFromStatement = ({ data }) => {
    return Axios.post(`/Invoices/rows/AddFromStatement`, data);
};

const useAddFromStatement = () => {
    return useMutation({
        mutationFn: onAddFromStatement,
        onSuccess: (_, variables) => {
            forceReloadInvoice(variables.data.InvoiceID);
        },
    });
};

const onDeleteRow = ({ data, type }) => {
    if (type === 'Statement') {
        return Axios.delete('/Invoices/rows/statement', { data });
    }
    return Axios.delete('/Invoices/rows/text', { data });
};

const useDeleteRow = () => {
    return useMutation({
        mutationFn: onDeleteRow,
        onSuccess: (_, variables) => {
            forceReloadInvoice(variables.data.InvoiceID);
        },
    });
};

const onDeleteInvoice = (ID) => {
    return Axios.delete(`/Invoices/${ID}`);
};

const useDeleteInvoice = () => {
    return useMutation({
        mutationFn: onDeleteInvoice,
        onSuccess: () => {},
    });
};

const onEditText = ({ data, type }) => {
    if (type === 'Statement') return Axios.put('/Invoices/rows/SetStatementText', data);
    if (type === 'Text') return Axios.put('/Invoices/rows/SetText', data);
    return Axios.put('/Invoices/rows/SetGroupText', data);
};

const useEditText = () => {
    return useMutation({
        mutationFn: onEditText,
        onSuccess: (_, variables) => {
            forceReloadInvoice(variables.data.InvoiceID);
            forceReloadCreditNote(variables.data.InvoiceID);
        },
    });
};

const onRemoveFromGroup = ({ data }) => {
    return Axios.put('/Invoices/rows/RemoveFromGroup', data);
};

const useRemoveFromGroup = () => {
    return useMutation({
        mutationFn: onRemoveFromGroup,
        onSuccess: (_, variables) => {
            forceReloadInvoice(variables.data.InvoiceID);
        },
    });
};

const onUpdatePaymentStatus = ({ data }) => {
    return Axios.put('/Invoices/SetPaymentStatus', data);
};

const useUpdatePaymentStatus = () => {
    return useMutation({
        mutationFn: onUpdatePaymentStatus,
        onSuccess: (_, variables) => {
            forceReloadInvoice(variables.data.InvoiceID);
        },
    });
};

const onInvoiceConfirm = ({ data }) => {
    return Axios.put('/Invoices/Confirm', data);
};

const useInvoiceConfirm = () => {
    return useMutation({
        mutationFn: onInvoiceConfirm,
        onSuccess: (_, variables) => {
            forceReloadInvoice(variables.data.InvoiceID);
        },
    });
};

const onClearInvoiceConfirm = (ID) => {
    return Axios.put(`/Invoices/Unconfirm/${ID}`);
};

const useClearInvoiceConfirm = () => {
    return useMutation({
        mutationFn: onClearInvoiceConfirm,
        onSuccess: (_, ID) => {
            forceReloadInvoice(ID);
        },
    });
};

export {
    forceReloadInvoice,
    forceReloadInvoicePrints,
    forceReloadInvoices,
    forceReloadSearchInvoices,
    refetchInvoicesSearch,
    useAddFromStatement,
    useAddManual,
    useAddNewInvoice,
    useClearInvoiceConfirm,
    useDeleteInvoice,
    useDeleteRow,
    useEditText,
    useGroupRows,
    useInvoice,
    useInvoicePrints,
    useInvoiceConfirm,
    useInvoices,
    useRemoveFromGroup,
    useSearchInvoices,
    useSortRows,
    useUnGroupRows,
    useUpdateInvoice,
    useUpdatePaymentStatus,
};
